import { ref, watch } from "vue";

/**
 * Erreurs de formulaire qui s'EFFACENT dès qu'on corrige le champ.
 *
 * 🔴 Karim 2026-09-26 : après un clic sur « Continuer », un champ cerné de
 * rouge le restait pendant qu'on tapait dedans — jusqu'au clic suivant. On
 * corrigeait son nom et le formulaire continuait d'affirmer qu'il manquait.
 *
 * Règle, identique dans tous les tunnels publics (don, adhésion, billetterie,
 * boutique) :
 *  · les erreurs sont calculées au clic, par l'étape (inchangé) ;
 *  · dès que la VALEUR d'un champ fautif change, son erreur disparaît ;
 *  · elle ne revient qu'au clic suivant, si le champ est toujours invalide.
 *
 * On n'essaie PAS de revalider en direct : « prénom obligatoire » qui
 * clignote à chaque caractère effacé, ou « email invalide » pendant qu'on
 * tape encore l'adresse, est pire que le rouge qui s'attarde.
 *
 * @param erreurs Erreurs du dernier clic, par clé de champ. Un NOUVEL objet à
 *   chaque validation : c'est ce changement de référence qui réarme l'effacement.
 * @param valeurs Valeurs courantes, mêmes clés que `erreurs`.
 */
export function useErreursEffaceesALaSaisie(
  erreurs: () => Record<string, string> | undefined,
  valeurs: () => Record<string, unknown> | undefined,
) {
  /** Champs modifiés depuis le dernier clic : leur erreur est masquée. */
  const corriges = ref(new Set<string>());

  /** Valeur de chaque champ fautif au moment du clic (sérialisée). */
  let auClic: Record<string, string> = {};

  // Tableaux (choix multiple) et dates se comparent par valeur, pas par
  // référence : un tableau recréé à l'identique n'est pas une correction.
  const empreinte = (v: unknown) => JSON.stringify(v ?? null);

  watch(
    erreurs,
    (e) => {
      const v = valeurs() ?? {};
      auClic = Object.fromEntries(
        Object.keys(e ?? {}).map((cle) => [cle, empreinte(v[cle])]),
      );
      corriges.value = new Set();
    },
    { immediate: true },
  );

  watch(
    valeurs,
    (v) => {
      for (const cle of Object.keys(auClic)) {
        if (corriges.value.has(cle)) continue;
        if (empreinte(v?.[cle]) !== auClic[cle]) {
          corriges.value = new Set(corriges.value).add(cle);
        }
      }
    },
    { deep: true },
  );

  function erreurDe(cle: string): string | undefined {
    if (corriges.value.has(cle)) return undefined;
    return erreurs()?.[cle];
  }

  return { erreurDe };
}
